import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import MainHeader, { MENU_REPORT } from "../../components/common/MainHeader";
import { WOOD_IMPORT_GRADE_DISPLAY } from "../../constance/dataTypes";
import moment from "moment";
import ReportDetail from "../../components/reports/ReportDetail";
import { URL_BARCODE } from "../../constance/urls";
import { formatComma } from "../../utils/stringFormat";
import ReportExportButton from "../../components/common/ReportExportButton";
import { Row, Col, DatePicker, Select, Button, Form, Typography } from "antd";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import dayjs from "dayjs";

export default function ReportWoodImportByCompanyDate() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = React.useState(null);

  const ref = useRef();
  const [form] = Form.useForm();
  const date = Form.useWatch("dateRange", form);
  const grade = Form.useWatch("wood_grade_options", form);
  const [errors, setErrors] = useState({});

  const columns = React.useMemo(
    () => [
      {
        title:  t("truck_companies.code"),
        dataIndex: "truck_company_code",
      },
      {
        title:  t("truck_companies.name"),
        dataIndex: "truck_company_name",
      },
      {
        title: t("date"),
        dataIndex: "date",
        render: (text, record) => moment(record.date).format(HUMAN_DATE_FORMAT),
      },
      {
        title: t("wood_import_customer.document_no"),
        dataIndex: "order_no",
      },
      {
        title: t("wood_import_customer.sent_qty"),
        dataIndex: "total_woods",
        render: (text, record) => formatComma(record.total_woods, 0),
      },
      {
        title: t("wood_import_customer.good_wood_qty"),
        dataIndex: "good_woods",
        render: (text, record) => formatComma(record.good_woods, 0),
      },
      {
        title: t("wood_import_customer.volume_cuft"),
        dataIndex: "good_volume",
        render: (text, record) => formatComma(record.good_volume),
      },
      {
        title: t("wood_import_customer.waste_wood_qty"),
        dataIndex: "waste_woods",
        render: (text, record) => formatComma(record.waste_woods, 0),
      },
      {
        title: t("wood_import_customer.volume_cuft"),
        dataIndex: "waste_woods",
        render: (text, record) => formatComma(record.waste_woods, 0),
      },
      {
        title:  t("wood_import_customer.total_volume_cuft"),
        dataIndex: "total_volume",
        render: (text, record) => formatComma(record.total_volume),
      },
      {
        title: t("wood_imports.buy_price"),
        dataIndex: "buy_price",
        render: (text, record) =>
          record.buy_price ? formatComma(record.buy_price) : "",
      },
      {
        title: t("wood_import_customer.purchase_amount"),
        dataIndex: "buy_amount",
        render: (text, record) => formatComma(record.buy_amount),
      },
      {
        title: t("wood_imports.sell_price"),
        dataIndex: "sell_price",
        render: (text, record) =>
          record.sell_price ? formatComma(record.sell_price) : "",
      },
      {
        title: t("wood_import_customer.purchase_amount"),
        dataIndex: "sell_amount",
        render: (text, record) => formatComma(record.sell_amount),
      },
      {
        title: t("wood_import_customer.profit_diff"),
        dataIndex: "diff_price",
        render: (text, record) =>
          record ? formatComma(record.diff_price) : "",
      },
      {
        title: "ส่วนต่างจำนวนเงิน ขาย-ซื้อ (บาท)",
        dataIndex: "diff_amount",
        render: (text, record) => formatComma(record.diff_amount),
      },
    ],
    [t]
  );

  const wood_grade_options = WOOD_IMPORT_GRADE_DISPLAY.map((grade) => ({
    value: grade,
    key: grade,
    label: grade.toLocaleUpperCase(),
  }));

  const getQueryParams = () => {
    const values = form.getFieldsValue();
    const startDate = dayjs(values.dateRange[0]).format(DATE_FORMAT);
    const endDate = dayjs(values.dateRange[1]).format(DATE_FORMAT);
    const queryString = `date_after=${startDate}&date_before=${endDate}&grade=${grade}`;
    return queryString;
  };

  const validateForm = () => {
    const values = form.getFieldsValue();
    const newErrors = {};
    if (!values.dateRange || values.dateRange.length < 2)
      newErrors.dateRange = t("error_messages.validation.required");
    if (!grade) newErrors.grade = t("error_messages.validation.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  React.useEffect(() => {
    if (validateForm()) {
      if (ref.current) {
        const params = getQueryParams();
        ref.current.refetch(params);
        setQueryParams(params);
      }
    }
  }, [date, grade]);

  return (
    <div style={{ margin: "24px", marginTop: "7em" }}>
      <MainHeader activeItem={MENU_REPORT} />

      <Typography.Title level={2}>
        {t(
          "menus.report_menus.wood_import_menus.wood_import_by_customer_and_date"
        )}
      </Typography.Title>

      <Form
        layout="horizontal"
        form={form}
        initialValues={{
          wood_grade_options: "ab",
          dateRange: [],
        }}
      >
        <Row gutter={16}>
          <Col sm={24} md={24} lg={6}>
            <Form.Item
              label={t("filter")}
              name="dateRange"
              rules={[{ required: true, message: t("กรุณาเลือกช่วงเวลา") }]}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                placeholder={[
                  t("searchs.placeholders.startDate"),
                  t("searchs.placeholders.endDate"),
                ]}
                format={HUMAN_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
          <Col sm={24} md={24} lg={6}>
            <Form.Item
              label={t("wood_imports.grade")}
              name="wood_grade_options"
            >
              <Select options={wood_grade_options} />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ textAlign: "right" }}>
          {["pdf", "excel"].map((type) => (
            <ReportExportButton
              key={type}
              url={`${URL_BARCODE.REPORT_WOOD_IMPORT_CUSTOMER_DATE_EXPORT}${type}/?${queryParams}`}
              type={type}
              title={`${t(
                "menus.report_menus.wood_import_menus.wood_import_by_customer_and_date"
              )}.....`}
              disabled={queryParams == null}
            />
          ))}
        </div>
      </Form>
      <ReportDetail
        ref={ref}
        reportUrl={URL_BARCODE.REPORT_WOOD_IMPORT_CUSTOMER_DATE}
        columns={columns}
        isCustomSetData
        customSetData={(datas) => {
          const newData = [];
          for (let data of datas) {
            for (let [index, transaction_data] of data.report.entries()) {
              if (index == 0) {
                newData.push({
                  truck_company_code: data.truck_company_code,
                  truck_company_name: data.truck_company_name,
                  ...transaction_data,
                });
              } else {
                newData.push(transaction_data);
              }
            }
            newData.push({
              order_no: "รวม",
              total_woods: data.report.reduce(
                (prev, cur) => prev + Number(cur.total_woods),
                Number(0)
              ),
              total_volume: data.report.reduce(
                (prev, cur) => prev + Number(cur.total_volume),
                Number(0)
              ),
              good_woods: data.report.reduce(
                (prev, cur) => prev + Number(cur.good_woods),
                Number(0)
              ),
              good_volume: data.report.reduce(
                (prev, cur) => prev + Number(cur.good_volume),
                Number(0)
              ),
              waste_woods: data.report.reduce(
                (prev, cur) => prev + Number(cur.waste_woods),
                Number(0)
              ),
              waste_volume: data.report.reduce(
                (prev, cur) => prev + Number(cur.waste_volume),
                Number(0)
              ),
              buy_amount: data.report.reduce(
                (prev, cur) => prev + Number(cur.buy_amount),
                Number(0)
              ),
              sell_amount: data.report.reduce(
                (prev, cur) => prev + Number(cur.sell_amount),
                Number(0)
              ),
              diff_amount: data.report.reduce(
                (prev, cur) => prev + Number(cur.diff_amount),
                Number(0)
              ),
            });
          }
          return newData;
        }}
      />
    </div>
  );
}
